import cn from "classnames";
import { DateTimeModel } from "entities/shared/shipmentCard.types";
import { resolveDateValues } from "entities/shared/utils";
import { Nullable } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { Button, ButtonIcon } from "shared/ui/Button";
import { Tooltip } from "shared/ui/Tooltip";
import { CardRow } from "./CardRow";
import styles from "../Card.module.scss";

import { useCardContext } from "../Card.context";
export type CardFooterProps = {
  etd?: DateTimeModel | null;
  eta?: DateTimeModel | null;
  atd?: DateTimeModel | null;
  ata?: DateTimeModel | null;
  predictiveETA?: DateTimeModel | null;
  reference?: Nullable<string>;
  className?: string;
};

export const CardFooter: RFC<CardFooterProps> = ({ atd, etd, ata, eta, predictiveETA, reference, className }) => {
  const { t } = useTranslation();
  const { onDelete, onOpen, onFollow, isDeleteProcessing, isFollowLoading } = useCardContext();

  const { departureDate, arrivalDate } = resolveDateValues({
    atd,
    etd,
    ata,
    eta,
    predictiveETA,
    useValuePlaceholder: true,
    formatedDate: true,
  });

  const hasTooltip = arrivalDate.isPredictive;

  return (
    <CardRow className={cn(styles.footer, className)}>
      <div className={styles.dateBox}>
        <div className={styles.date}>
          {departureDate.unit}
          {departureDate.value ? ` ${departureDate.value}` : departureDate.value}
        </div>

        <Tooltip
          className={cn(styles.date, hasTooltip && styles.dateWithTooltip)}
          triggerElement={
            <div>
              {arrivalDate.unit}
              {arrivalDate.value ? ` ${arrivalDate.value}` : arrivalDate.value}
            </div>
          }
          offsetY={5}
          disabled={!hasTooltip}
        >
          <p className={styles.tooltipDateText}>{t("Calculated based on historical data from shipping line")}</p>
        </Tooltip>
      </div>

      <div className={styles.buttonBox}>
        {onFollow && (
          <Button
            disabled={isFollowLoading}
            variant="pale"
            size="tiny"
            className={cn(styles.button, styles.follow)}
            onClick={onFollow}
            data-test-id={`card-action-follow-${reference}`}
          >
            {t("Follow")}
          </Button>
        )}

        {onDelete && (
          <ButtonIcon
            disabled={isDeleteProcessing}
            icon="trash"
            size="tiny"
            className={cn(styles.button, styles.delete)}
            onClick={onDelete}
            data-test-id={`card-action-delete-${reference}`}
          />
        )}

        {onOpen && (
          <ButtonIcon
            icon="arrow-right"
            size="tiny"
            className={cn(styles.button, styles.open)}
            onClick={onOpen}
            data-test-id={`card-action-open-${reference}`}
          />
        )}
      </div>
    </CardRow>
  );
};

CardFooter.displayName = CardFooter.name;
